import type { Vec2 } from '@learnforge/demo-math/geometry';
import { createSvgEl } from '@learnforge/demo-render';

/**
 * Anything that can map a world point to screen coordinates.
 * `GridHandle` satisfies this interface, and so does a dual-pane sub-region.
 */
export interface Projector {
  worldToScreen(p: Vec2): Vec2;
}

export interface CurveLayerHandle {
  /** Replace the polyline with screen-projected points, splitting at non-finite values and declared x discontinuities. */
  setPolyline(
    id: string,
    worldPoints: readonly Vec2[],
    className: string,
    discontinuities?: readonly number[],
  ): void;
  /** Remove a previously-set polyline. */
  clear(id: string): void;
  /** The owning <g>, for z-order assertions. */
  readonly group: SVGGElement;
  destroy(): void;
}

/**
 * Create a curve layer that renders function plots as SVG <path> elements.
 * The layer appends a <g> to `parent`; caller should insert it in the correct
 * z-order (after the grid, before the figure).
 */
export function createCurveLayer(parent: SVGElement, projector: Projector): CurveLayerHandle {
  const group = createSvgEl('g');
  parent.appendChild(group);

  const paths = new Map<string, SVGPathElement>();

  function buildD(worldPoints: readonly Vec2[], discontinuities: readonly number[]): string {
    let d = '';
    let penDown = false;
    let previousX: number | null = null;

    for (const wp of worldPoints) {
      // Break the path at non-finite y values (asymptotes / holes).
      if (!Number.isFinite(wp.y)) {
        penDown = false;
        previousX = wp.x;
        continue;
      }

      if (
        previousX !== null &&
        discontinuities.some(
          (x) => x > Math.min(previousX!, wp.x) && x <= Math.max(previousX!, wp.x),
        )
      ) {
        penDown = false;
      }

      const sp = projector.worldToScreen(wp);

      if (!penDown) {
        d += `M${sp.x} ${sp.y}`;
        penDown = true;
      } else {
        d += `L${sp.x} ${sp.y}`;
      }
      previousX = wp.x;
    }

    return d;
  }

  return {
    get group() {
      return group;
    },

    setPolyline(
      id: string,
      worldPoints: readonly Vec2[],
      className: string,
      discontinuities: readonly number[] = [],
    ): void {
      let path = paths.get(id);
      if (!path) {
        path = createSvgEl('path');
        group.appendChild(path);
        paths.set(id, path);
      }
      path.setAttribute('class', className);
      path.setAttribute('d', buildD(worldPoints, discontinuities));
    },

    clear(id: string): void {
      const path = paths.get(id);
      if (path) {
        path.remove();
        paths.delete(id);
      }
    },

    destroy(): void {
      group.remove();
      paths.clear();
    },
  };
}
